import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { NotificationType } from 'angular2-notifications'
import { catchError, map, Observable, of, retry, switchMap, tap, throwError, timer, withLatestFrom } from 'rxjs'
import { AppErrorHandlerService } from '../../services/app-error-handler/app-error-handler.service'
import { LogService } from '../../services/log.service'
import { NotificationService } from '../../services/notification.service'
import { RetryConnectionService } from '../../services/retry-connection.service'
import * as FeatureEPActions from '../feature-edit-project/feature-ep.actions'
import { getNumberOfYourDrafts, searchYourDraft } from '../feature-manage-your-drafts/feature-myd.actions'
import { searchYourProjects } from '../feature-manage-your-projects/feature-myp.actions'
import { selectMYPSearchRequest } from '../feature-manage-your-projects/feature-myp.selectors'
import { search } from '../feature-search-projects/feature-sp.actions'
import { selectSearchRequest } from '../feature-search-projects/feature-sp.selectors'
import * as StageActions from './stage.actions'
import { getPathLocationPrevious } from './stage.selectors'
import { StageService } from './stage.service'
import { SearchYourProjectsFields } from '../../../features/feature-manage-projects/models/mp-search-your-projects-fields.interface'
import { BasicRequest } from '../../models/interfaces/basic-request.interface'
import { selectMYDSearchRequest } from '../feature-manage-your-drafts/feature-myd.selectors'

@Injectable()
export class StageEffects {
   private _cn = this.constructor.name
   private _store = inject(Store)
   private _router = inject(Router)
   private _actions$ = inject(Actions)
   private _logService = inject(LogService)
   private _stageService = inject(StageService)
   private _translate = inject(TranslateService)
   private _notificationService = inject(NotificationService)
   private _errorHandlerService = inject(AppErrorHandlerService)
   private _retryNotificationService = inject(RetryConnectionService)

   private _retryGetRequest<T>(request$: Observable<T>) {
      let hasRetried = false
      let hasNotifiedRetryStart = false

      return request$.pipe(
         retry({
            count: 3,
            delay: (_error: HttpErrorResponse, retryCount: number) => {
               hasRetried = true

               if (!hasNotifiedRetryStart) {
                  hasNotifiedRetryStart = true
                  this._store.dispatch(StageActions.retryConnectionStarted())
               }

               return timer(retryCount === 1 ? 0 : 3000)
            },
         }),
         tap(() => {
            if (hasRetried) {
               this._store.dispatch(StageActions.retryConnectionSuccess())
            }
         }),
         catchError((error: HttpErrorResponse) => {
            if (hasRetried) {
               this._store.dispatch(StageActions.retryConnectionFailure())
            }

            return throwError(() => error)
         })
      )
   }

   loadStatutes$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setStatuses),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getStatuses()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadStatutes effect', 'response:', res)),
               map((statuses) => StageActions.setStatusesSuccess({ statuses })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setStatusesFailure({ error: error }))
               })
            )
         )
      )
   )

   loadVisibilities$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setVisibilities),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getVisibilities()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadVisibilities effect', 'response:', res)),
               map((visibilities) => StageActions.setVisibilitiesSuccess({ visibilities })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setVisibilitiesFailure({ error: error }))
               })
            )
         )
      )
   )

   loadMeasuringPurpose$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setMeasuringPurpose),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getMeasuringPurposes()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadMeasuringPurpose effect', 'response:', res)),
               map((measuringPurpose) => StageActions.setMeasuringPurposeSuccess({ measuringPurpose })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setMeasuringPurposeFailure({ error: error }))
               })
            )
         )
      )
   )

   loadSectors$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setSectors),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getSectors()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadSectors effect', 'response:', res)),
               map((sectors) => StageActions.setSectorsSuccess({ sectors })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setSectorsFailure({ error: error }))
               })
            )
         )
      )
   )

   loadTechnologies$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setTechnologies),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getTechnologies()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadTechnologies effect', 'response:', res)),
               map((technologies) => StageActions.setTechnologiesSuccess({ technologies })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setTechnologiesFailure({ error: error }))
               })
            )
         )
      )
   )

   loadCountries$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setCountries),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getCountries()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadCountries effect', 'response:', res)),
               map((countries) => StageActions.setCountriesSuccess({ countries })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setCountriesFailure({ error: error }))
               })
            )
         )
      )
   )

   loadPNOCountryResponsibles$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.setPNOCountryResponsibles),
         switchMap(() =>
            this._retryGetRequest(this._stageService.getPNOCountryResponsibles()).pipe(
               tap((res) => this._logService.debug(this._cn, 'loadPNOCountryResponsibles effect', 'response:', res)),
               map((pnoCountryResponsibles) => StageActions.setPNOCountryResponsiblesSuccess({ pnoCountryResponsibles })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.setPNOCountryResponsiblesFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteProject$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.deleteProject),
         switchMap((action) =>
            this._stageService.deleteProject(action.id, action.status).pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteProject effect', 'response:', res)),
               map(() => StageActions.deleteProjectSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.deleteProjectFailure({ error: error }))
               })
            )
         )
      )
   )

   chnagePassword$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.changePassword),
         switchMap((action) =>
            this._stageService.changePassword(action.request).pipe(
               tap((res) => this._logService.debug(this._cn, 'change password effect', 'response:', res)),
               map(() => {
                  this._router.navigate(['/'])
                  this._notificationService.notify(NotificationType.Success, undefined, this._translate.instant('password_changed_with_success'))
                  return StageActions.changePasswordSuccess()
               }),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.changePasswordFailure({ error: error }))
               })
            )
         )
      )
   )

   loadAccountInfo$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.getAccountInfo),
         switchMap(() =>
            this._stageService.getAccountInfo().pipe(
               tap((res) => this._logService.debug(this._cn, 'loadAccountInfo effect', 'response:', res)),
               map((accountInfo) => StageActions.getAccountInfoSuccess({ accountInfo })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.getAccountInfoFailure({ error: error }))
               })
            )
         )
      )
   )

   updateAccountInfo$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.updateAccountInfo),
         switchMap((action) =>
            this._stageService.updateAccountInfo(action.accountInfo).pipe(
               tap((res) => this._logService.debug(this._cn, 'updateAccountInfo effect', 'response:', res)),
               map(() => {
                  this._router.navigate(['/'])
                  this._notificationService.notify(NotificationType.Success, undefined, this._translate.instant('updated_account_info_with_success'))
                  return StageActions.updateAccountInfoSuccess()
               }),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.updateAccountInfoFailure({ error: error }))
               })
            )
         )
      )
   )

   deleteAvatar$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.deleteAvatar),
         switchMap(() =>
            this._stageService.deleteAvatar().pipe(
               tap((res) => this._logService.debug(this._cn, 'deleteAvatar effect', 'response:', res)),
               map(() => StageActions.deleteAvatarSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.deleteAvatarFailure({ error: error }))
               })
            )
         )
      )
   )

   getAvatar$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.getAvatar),
         switchMap(() =>
            this._stageService.getAvatar().pipe(
               tap((res) => this._logService.debug(this._cn, 'loadAccountInfo effect', 'response:', res)),
               map((res) => StageActions.getAvatarSuccess({ avatarFile: res })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.getAccountInfoFailure({ error: error }))
               })
            )
         )
      )
   )

   updateAvatar$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.updateAvatar),
         switchMap((action) =>
            this._stageService.updateAvatar(action.file).pipe(
               tap((res) => this._logService.debug(this._cn, 'updateAvatar effect', 'response:', res)),
               map(() => StageActions.updateAvatarSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.updateAvatarFailure({ error: error }))
               })
            )
         )
      )
   )

   loadPNOUserNotifications$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.getPNOUserNotifications),
         switchMap(() =>
            this._stageService.getPNOUserNotifications().pipe(
               tap((res) => this._logService.debug(this._cn, 'loadPNOUserNotifications effect', 'response:', res)),
               map((userNotifications) => StageActions.getPNOUserNotificationsSuccess({ userNotifications })),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.getPNOUserNotificationsFailure({ error: error }))
               })
            )
         )
      )
   )

   updatePNOUserNotifications$ = createEffect(() =>
      this._actions$.pipe(
         ofType(StageActions.updatePNOUserNotifications),
         switchMap((action) =>
            this._stageService.updatePNOUserNotifications(action.notificationName, action.isActive).pipe(
               tap((res) => this._logService.debug(this._cn, 'updatePNOUserNotifications effect', 'response:', res)),
               map(() => StageActions.updatePNOUserNotificationsSuccess()),
               catchError((error: HttpErrorResponse) => {
                  return of(StageActions.updatePNOUserNotificationsFailure({ error: error }))
               })
            )
         )
      )
   )

   listnerForAvatarChanges$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(StageActions.deleteAvatarSuccess, StageActions.updateAvatarSuccess),
            map(() => this._store.dispatch(StageActions.getAccountInfo()))
         ),
      { dispatch: false }
   )

   private _actionsForErrorNotificationMessage = [
      StageActions.setStatusesFailure,
      StageActions.setVisibilitiesFailure,
      StageActions.setMeasuringPurposeFailure,
      StageActions.deleteProjectFailure,
      StageActions.changePasswordFailure,
      StageActions.getAccountInfoFailure,
      StageActions.updateAccountInfoFailure,
      StageActions.deleteAvatarFailure,
      StageActions.getAccountInfoFailure,
      StageActions.updateAvatarFailure,
      StageActions.setSectorsFailure,
      StageActions.setTechnologiesFailure,
      StageActions.setCountriesFailure,
      StageActions.setPNOCountryResponsiblesFailure,
      StageActions.getPNOUserNotificationsFailure,
      StageActions.updatePNOUserNotificationsFailure,
   ]

   listenAllFailureAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForErrorNotificationMessage),
            map((errorResponse) => this._errorHandlerService.handleError(errorResponse.error))
         ),
      { dispatch: false }
   )

   private _actionsForSuccessNotificationMessageAndRefreshConditions = [StageActions.deleteProjectSuccess]

   listenAllSuccessActionWithRefreshCondtions$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForSuccessNotificationMessageAndRefreshConditions),
            withLatestFrom(
               this._store.pipe(select(selectSearchRequest)),
               this._store.pipe(select(selectMYPSearchRequest)),
               this._store.pipe(select(selectMYDSearchRequest)),
               this._store.pipe(select(getPathLocationPrevious))
            ),
            map((value) => {
               this._notificationService.notify(NotificationType.Success, undefined, this._translate.instant('operation_executed_with_success'))

               if (this._router.url.includes('manage-projects') && value[2]) {
                  this._store.dispatch(searchYourProjects({ request: value[2] }))
                  return
               }
               if (this._router.url.includes('manage-drafts') && value[3]) {
                  this._store.dispatch(searchYourDraft({ request: value[3] }))
                  return
               }
               if (this._router.url.includes('search') && value[1]) {
                  this._store.dispatch(search({ request: value[1] }))
                  return
               }
               value[4]?.includes('search') ? this._router.navigate([`/${value[4]}`]) : this._router.navigate(['/'])
            })
         ),
      { dispatch: false }
   )

   private _actionsForSuccessNotificationMessage = [StageActions.updatePNOUserNotificationsSuccess]

   listenAllSuccessAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForSuccessNotificationMessage),
            map(() => this._notificationService.notify(NotificationType.Success, undefined, this._translate.instant('operation_executed_with_success')))
         ),
      { dispatch: false }
   )

   private _actionsForRetryConnectionNotificationMessage = [StageActions.retryConnectionStarted]

   listenAllRetryConnectionAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForRetryConnectionNotificationMessage),
            map(() => this._retryNotificationService.addMessages('warn', 'Trying to reconnect...'))
         ),
      { dispatch: false }
   )

   private _actionsForRetryConnectionSuccessNotificationMessage = [StageActions.retryConnectionSuccess]

   listenAllRetryConnectionSuccessAction$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForRetryConnectionSuccessNotificationMessage),
            map(() => {
               this._retryNotificationService.addMessages('success', 'Reconnected successfully')
               setTimeout(() => {
                  this._retryNotificationService.clearMessages()
               }, 1500)
            })
         ),
      { dispatch: false }
   )

   private _actionsForRetryConnectionFailedNotificationMessage = [StageActions.retryConnectionFailure]

   _actionsForRetryConnectionFailedNotificationMessage$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsForRetryConnectionFailedNotificationMessage),
            map(() => this._retryNotificationService.addMessages('error', 'There seems to be a connection problem. Please try again in a few minutes.'))
         ),
      { dispatch: false }
   )

   private _actionsUpdateDraftNumber = [
      FeatureEPActions.createProjectSuccess,
      FeatureEPActions.editProjectSuccess,
      FeatureEPActions.createDraftSuccess,
      FeatureEPActions.editDraftSuccess,
      StageActions.deleteProjectSuccess,
   ]

   updateDraftNumber$ = createEffect(
      () =>
         this._actions$.pipe(
            ofType(...this._actionsUpdateDraftNumber),
            map((res) => {
               this._store.dispatch(getNumberOfYourDrafts())
            })
         ),
      { dispatch: false }
   )
}
